import React, { useState, useCallback } from "react";

import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";

import { useAuthForm } from "src/domains/auth/hooks/useAuthForm";
import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { LoginCredentials } from "src/domains/auth/services/authService";
import { AUTH } from "src/platform/constants/routes";

import GithubButton from "./GithubButton";
import GoogleButton from "./GoogleButton";

export const SignInForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { login, authInProgress } = useLegacyAuth();
  const { formData, errors, handleChange, validate, reset } = useAuthForm();

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
      height: "38px",
      border: `1px solid ${isDark ? theme.palette.grey[600] : theme.palette.grey[200]}`,
      transition: "all 150ms ease",
      "&:hover": {
        borderColor: isDark ? theme.palette.grey[500] : theme.palette.grey[300],
      },
      "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
      },
      "& fieldset": { border: "none" },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      "&.Mui-focused": { color: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "14px",
      padding: "10px 14px",
    },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.secondary,
    },
    "& .MuiFormHelperText-root": { color: theme.palette.text.secondary },
  } as const;

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(true);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const validationResult = validate();
      if (!validationResult.isValid) return;

      try {
        const credentials: LoginCredentials = {
          email: formData.email,
          password: formData.password,
        };
        await login(credentials);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to sign in");
        reset();
      }
    },
    [formData, login, reset, validate],
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleChange("email")(e),
    [handleChange],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleChange("password")(e),
    [handleChange],
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {/* Welcome heading */}
      <Typography
        component="h1"
        fontWeight={700}
        sx={{ color: "text.primary", mb: 1, fontSize: "1.5rem" }}
      >
        Sign in to Rusha
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Access your organizations, projects, and applications
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            borderRadius: "8px",
            py: 1.5,
            px: 2,
            fontSize: "13px",
            "& .MuiAlert-icon": { display: "none" },
          }}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Email
            </Typography>
            <TextField
              id="email"
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              placeholder="you@company.com"
              disabled={authInProgress}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Password
            </Typography>
            <TextField
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={authInProgress}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      tabIndex={-1}
                      size="small"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                      sx={{
                        color: "text.secondary",
                        borderRadius: "6px",
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                size="small"
                sx={{
                  color: "text.secondary",
                  padding: 0,
                  width: 16,
                  height: 16,
                  "& .MuiSvgIcon-root": { fontSize: 16 },
                  "&.Mui-checked": { color: "primary.main" },
                }}
              />
            }
            label={
              <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>
                Remember me
              </Typography>
            }
            sx={{ gap: "8px", margin: 0 }}
          />
          <Link
            component="button"
            type="button"
            onClick={() => navigate(AUTH.LOGIN.path)}
            sx={{
              fontSize: "13px",
              color: "primary.dark",
              fontWeight: 400,
            }}
          >
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          disabled={authInProgress}
          sx={{
            padding: "12px 16px",
            minHeight: "41px",
            borderRadius: "10px",
            border: "none",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9375rem",
            color: theme.palette.primary.contrastText,
            background: theme.palette.primary.dark,
            boxShadow: "0 2px 12px rgba(0, 212, 255, 0.25)",
            transition: "all 150ms ease",
            "&:hover": {
              background: theme.palette.primary.dark,
              boxShadow: "0 4px 16px rgba(0, 212, 255, 0.35)",
            },
            "&:active": { transform: "scale(0.98)" },
            "&:disabled": {
              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
              color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
            },
          }}
        >
          {authInProgress ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress
                size={16}
                color="inherit"
                aria-label="Signing in"
              />
              <span>Signing in...</span>
            </Box>
          ) : (
            "Sign in"
          )}
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            my: 2.5,
            color: "text.secondary",
          }}
        >
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            Or use a brokered customer identity provider
          </Typography>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <GoogleButton disabled={authInProgress} />
          <GithubButton disabled={authInProgress} />
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>
            Don&apos;t have an account?{" "}
            <Link
              component="button"
              type="button"
              onClick={() => navigate(AUTH.REGISTER.path)}
              sx={{
                fontSize: "13px",
                color: "primary.dark",
                fontWeight: 400,
              }}
            >
              Create one
            </Link>
          </Typography>
        </Box>
      </form>
    </Box>
  );
};
